import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, Save } from "lucide-react";

export default function ProfileEdit() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    judul_ta: user?.judul_ta || "",
    pembimbing1_id: user?.pembimbing1_id || "",
    pembimbing2_id: user?.pembimbing2_id || "",
    nip: user?.nip || "",
    whatsapp: user?.whatsapp || "",
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    setLoading(true);
    try {
      const payload = {};
      if (form.name !== user?.name) payload.name = form.name;
      if (form.email !== user?.email) payload.email = form.email;
      if (form.password) payload.password = form.password;
      if (form.whatsapp !== (user?.whatsapp || "")) payload.whatsapp = form.whatsapp;
      if (user?.role === "mahasiswa") {
        if (form.judul_ta !== user?.judul_ta) payload.judul_ta = form.judul_ta;
      }
      if (user?.role === "dosen") {
        if (form.nip !== (user?.nip || "")) payload.nip = form.nip;
      }
      if (Object.keys(payload).length === 0) {
        setMsg({ type: "info", text: "Tidak ada perubahan untuk disimpan." });
        return;
      }
      const { data } = await axios.put(`${API}/auth/profile`, payload);
      setUser(data);
      setForm(p => ({ ...p, password: "" }));
      setMsg({ type: "success", text: "Profil berhasil diperbarui!" });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Gagal memperbarui profil" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Edit Profil</h1>
        <p className="text-[#475569] mt-1">Perbarui informasi akun Anda</p>
      </div>

      {/* Avatar */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm flex items-center gap-4">
        <div className="w-14 h-14 rounded-sm bg-[#002147] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-xl">{user?.name?.[0]?.toUpperCase() || "U"}</span>
        </div>
        <div>
          <p className="font-bold text-[#0F172A]">{user?.name}</p>
          <p className="text-sm text-[#475569]">{user?.email}</p>
          {user?.nim && <p className="text-xs text-[#475569]">NIM: {user?.nim}</p>}
          {user?.nip && <p className="text-xs text-[#475569]">NIP: {user?.nip}</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Nama Lengkap</label>
          <input
            data-testid="profile-name"
            type="text"
            value={form.name}
            onChange={f("name")}
            required
            className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Email</label>
          <input
            data-testid="profile-email"
            type="email"
            value={form.email}
            onChange={f("email")}
            required
            className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">
            Nomor WhatsApp <span className="text-[#475569] normal-case tracking-normal font-normal">(opsional)</span>
          </label>
          <input
            data-testid="profile-whatsapp"
            type="tel"
            value={form.whatsapp}
            onChange={f("whatsapp")}
            placeholder="cth: 081234567890"
            className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">
            Password Baru <span className="text-[#475569] normal-case tracking-normal font-normal">(kosongkan jika tidak diubah)</span>
          </label>
          <div className="relative">
            <input
              data-testid="profile-password"
              type={showPass ? "text" : "password"}
              value={form.password}
              onChange={f("password")}
              placeholder="Minimal 6 karakter"
              className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147] pr-10"
            />
            <button type="button" onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#0F172A]">
              {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        {user?.role === "dosen" && (
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">NIP</label>
            <input
              data-testid="profile-nip"
              type="text"
              value={form.nip}
              onChange={f("nip")}
              className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
            />
          </div>
        )}

        {user?.role === "mahasiswa" && (
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#0F172A] mb-2">Judul Tugas Akhir</label>
            <input
              data-testid="profile-judul"
              type="text"
              value={form.judul_ta}
              onChange={f("judul_ta")}
              placeholder="Judul TA Anda"
              className="w-full border border-slate-200 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
            />
          </div>
        )}

        {msg.text && (
          <div data-testid="profile-msg" className={`p-3 rounded-sm text-sm ${
            msg.type === "success" ? "bg-green-50 border border-green-200 text-green-700"
            : msg.type === "error" ? "bg-red-50 border border-red-200 text-red-700"
            : "bg-blue-50 border border-blue-200 text-blue-700"
          }`}>
            {msg.text}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="profile-save-btn"
          className="flex items-center gap-2 bg-[#002147] hover:bg-[#001833] disabled:bg-slate-400 text-white px-6 py-2.5 rounded-sm font-semibold text-sm transition-all active:translate-y-px"
        >
          <Save size={15} />
          {loading ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </form>
    </div>
  );
}
